import type { ComposerTranslation } from 'vue-i18n'

/** 事件对象类型枚举 */
export enum ObjectType {
  Process = 1,
  File = 2,
  Registry = 3,
  Module = 4,
  Service = 5,
  Http = 6,
  Flow = 7,
  Vuln = 8,
  Device = 10,
  Authentication = 11,
  Wmi = 12,
  Script = 13,
  Dns = 14,
  Memory = 15,
  RemoteDevice = 16,
  SchedJob = 17,
  Thread = 18,
  Shellcode = 19,
  C2Channel = 20,
  User = 21,
}

/** i18n key map — mirrors ObjectActionMap, used as translation key fallback labels */
export const ObjectActionLabelMap: Record<ObjectType, Record<number, string>> = {
  [ObjectType.Process]: { 1: 'securityEvent.action.process.create', 2: 'securityEvent.action.process.terminate', 3: 'securityEvent.action.process.createRemoteThread' },
  [ObjectType.File]: { 1: 'securityEvent.action.file.create', 2: 'securityEvent.action.file.delete', 3: 'securityEvent.action.file.modify', 4: 'securityEvent.action.file.read', 5: 'securityEvent.action.file.write' },
  [ObjectType.Registry]: { 1: 'securityEvent.action.registry.add', 2: 'securityEvent.action.registry.keyEdit', 3: 'securityEvent.action.registry.remove', 4: 'securityEvent.action.registry.valueEdit' },
  [ObjectType.Module]: { 1: 'securityEvent.action.module.load' },
  [ObjectType.Service]: { 1: 'securityEvent.action.service.create', 2: 'securityEvent.action.service.delete', 3: 'securityEvent.action.service.start', 4: 'securityEvent.action.service.stop', 5: 'securityEvent.action.service.pause' },
  [ObjectType.Http]: { 1: 'securityEvent.action.http.access', 2: 'securityEvent.action.http.get', 3: 'securityEvent.action.http.post' },
  [ObjectType.Flow]: { 1: 'securityEvent.action.flow.start', 2: 'securityEvent.action.flow.message', 3: 'securityEvent.action.flow.end' },
  [ObjectType.Vuln]: { 1: 'securityEvent.action.vuln.exploit' },
  [ObjectType.Device]: { 1: 'securityEvent.action.device.write' },
  [ObjectType.Authentication]: { 1: 'securityEvent.action.auth.error', 2: 'securityEvent.action.auth.failure', 3: 'securityEvent.action.auth.success' },
  [ObjectType.Wmi]: { 1: 'securityEvent.action.wmi.query', 2: 'securityEvent.action.wmi.execute', 3: 'securityEvent.action.wmi.put' },
  [ObjectType.Script]: { 1: 'securityEvent.action.script.execute', 2: 'securityEvent.action.script.content' },
  [ObjectType.Dns]: { 1: 'securityEvent.action.dns.query', 2: 'securityEvent.action.dns.write' },
  [ObjectType.Memory]: { 1: 'securityEvent.action.memory.alloc', 2: 'securityEvent.action.memory.read', 3: 'securityEvent.action.memory.write' },
  [ObjectType.RemoteDevice]: { 1: 'securityEvent.action.remoteDevice.inbound', 2: 'securityEvent.action.remoteDevice.outbound' },
  [ObjectType.SchedJob]: { 1: 'securityEvent.action.schedJob.create' },
  [ObjectType.Thread]: { 1: 'securityEvent.action.thread.create', 2: 'securityEvent.action.thread.remoteCreate', 3: 'securityEvent.action.thread.setThreadCtx', 4: 'securityEvent.action.thread.apc' },
  [ObjectType.Shellcode]: { 1: 'securityEvent.action.shellcode.exec' },
  [ObjectType.C2Channel]: { 1: 'securityEvent.action.c2Channel.connect' },
  [ObjectType.User]: { 1: 'securityEvent.action.user.create' },
}

/** 对象动作类型枚举映射 */
export const ObjectActionMap: Record<ObjectType, Record<number, string>> = {
  [ObjectType.Process]: {
    1: 'securityEvent.action.process.create',
    2: 'securityEvent.action.process.terminate',
    3: 'securityEvent.action.process.createRemoteThread',
  },
  [ObjectType.File]: {
    1: 'securityEvent.action.file.create',
    2: 'securityEvent.action.file.delete',
    3: 'securityEvent.action.file.modify',
    4: 'securityEvent.action.file.read',
    5: 'securityEvent.action.file.write',
  },
  [ObjectType.Registry]: {
    1: 'securityEvent.action.registry.add',
    2: 'securityEvent.action.registry.keyEdit',
    3: 'securityEvent.action.registry.remove',
    4: 'securityEvent.action.registry.valueEdit',
  },
  [ObjectType.Module]: {
    1: 'securityEvent.action.module.load',
  },
  [ObjectType.Service]: {
    1: 'securityEvent.action.service.create',
    2: 'securityEvent.action.service.delete',
    3: 'securityEvent.action.service.start',
    4: 'securityEvent.action.service.stop',
    5: 'securityEvent.action.service.pause',
  },
  [ObjectType.Http]: {
    1: 'securityEvent.action.http.access',
    2: 'securityEvent.action.http.get',
    3: 'securityEvent.action.http.post',
  },
  [ObjectType.Flow]: {
    1: 'securityEvent.action.flow.start',
    2: 'securityEvent.action.flow.message',
    3: 'securityEvent.action.flow.end',
  },
  [ObjectType.Vuln]: {
    1: 'securityEvent.action.vuln.exploit',
  },
  [ObjectType.Device]: {
    1: 'securityEvent.action.device.write',
  },
  [ObjectType.Authentication]: {
    1: 'securityEvent.action.auth.error',
    2: 'securityEvent.action.auth.failure',
    3: 'securityEvent.action.auth.success',
  },
  [ObjectType.Wmi]: {
    1: 'securityEvent.action.wmi.query',
    2: 'securityEvent.action.wmi.execute',
    3: 'securityEvent.action.wmi.put',
  },
  [ObjectType.Script]: {
    1: 'securityEvent.action.script.execute',
    2: 'securityEvent.action.script.content',
  },
  [ObjectType.Dns]: {
    1: 'securityEvent.action.dns.query',
    2: 'securityEvent.action.dns.write',
  },
  [ObjectType.Memory]: {
    1: 'securityEvent.action.memory.alloc',
    2: 'securityEvent.action.memory.read',
    3: 'securityEvent.action.memory.write',
  },
  [ObjectType.RemoteDevice]: {
    1: 'securityEvent.action.remoteDevice.inbound',
    2: 'securityEvent.action.remoteDevice.outbound',
  },
  [ObjectType.SchedJob]: {
    1: 'securityEvent.action.schedJob.create',
  },
  [ObjectType.Thread]: {
    1: 'securityEvent.action.thread.create',
    2: 'securityEvent.action.thread.remoteCreate',
    3: 'securityEvent.action.thread.setThreadCtx',
    4: 'securityEvent.action.thread.apc',
  },
  [ObjectType.Shellcode]: {
    1: 'securityEvent.action.shellcode.exec',
  },
  [ObjectType.C2Channel]: {
    1: 'securityEvent.action.c2Channel.connect',
  },
  [ObjectType.User]: {
    1: 'securityEvent.action.user.create',
  },
}

/** 获取对象类型名称 */
export function getObjectTypeName(objectType: number | string, t?: ComposerTranslation): string {
  const type = Number(objectType)
  if (!t) return `Object_${type}`
  
  const key = `securityEvent.objectType.${type}`
  const translated = t(key)
  return translated !== key ? translated : `Object_${type}`
}

/** 获取动作名称 */
export function getActionName(objectType: number | string, action: number | string, t?: ComposerTranslation): string {
  const type = Number(objectType) as ObjectType
  const actionNum = Number(action)
  
  if (!t) return ObjectActionLabelMap[type]?.[actionNum] ?? `Action_${actionNum}`
  
  const actionMap = ObjectActionMap[type]
  if (!actionMap) return ObjectActionLabelMap[type]?.[actionNum] ?? `Action_${actionNum}`
  
  const key = actionMap[actionNum]
  if (!key) return ObjectActionLabelMap[type]?.[actionNum] ?? `Action_${actionNum}`
  
  const translated = t(key)
  return translated !== key ? translated : `Action_${actionNum}`
}

/** 节点角色类型 */
export enum NodeType {
  Normal = 1,
  Operator = 2,
  Target = 3,
}

/** 获取节点角色名称 */
export function getNodeRoleName(nodeType: number, t?: ComposerTranslation): string {
  if (!t) return nodeType === NodeType.Operator ? 'Operator' : nodeType === NodeType.Target ? 'Target' : 'Normal'
  
  const key = `securityEvent.nodeRole.${nodeType}`
  const translated = t(key)
  return translated !== key ? translated : ''
}
